// Open-position card — card-view counterpart of PositionTracker's Open
// Positions table row: side + venue badges, market header, qty/avg/now/cost
// stats, and a P&L / cash-out / if-win footer with a close-edit action.

import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { VenueChip } from '../atoms/VenueChip';
import { GameDetailLink } from '../atoms/GameDetailLink';
import {
  AnnotatedCompactHeader,
  AnnotatedControls,
  FamilyPendingMark,
  CardShell,
  CardHeader,
  CardBody,
  CardFooter,
  CompactStat,
  RestingCloseNote,
  PinToggle,
  StatLine,
  MarketTypeChip,
  SideBadge,
  BetTeamTitle,
  SAME_GAME_HIGHLIGHT,
} from './cardKit';
import {
  fmtCents,
  fmtLastFillAt,
  fmtQty,
  fmtUsd,
  fmtUsdSigned,
  fmtVol,
} from './cardFormat';
import {
  fullPositionSummary,
  nowTone,
  type FullPositionSummary,
  type PositionCardVM,
} from './positionCardModel';
import { DivergenceBadge } from './DivergenceBadge';
import { POLY_MID_STAT_LABEL, UPOddsValue, type UPOdds } from './upOdds';
import type { DivergenceInfo } from '@/lib/divergence/polyKalshiDivergence';
import { UsAccountWord } from '@/views/PositionTracker/usTrackerRows';

/** The held size: #1226 — a Polymarket US row's exact decimal text (with its
 *  partly-known word beside it); K/P rows keep the whole-number formatter.
 *  Org Detail's table (#1227) draws it too. */
export function PositionQty({ vm }: { vm: PositionCardVM }) {
  return positionQty(vm);
}

function positionQty(vm: PositionCardVM) {
  if (vm.qtyText === undefined) return fmtQty(vm.qty);
  return (
    <>
      {vm.us?.partialText && (
        <span className="mr-1.5 font-sans text-[10px] font-medium text-amber-400 light:text-amber-700" title={vm.us.partialTitle ?? undefined}
          data-testid="us-position-partial">
          {vm.us.partialText}
        </span>
      )}
      {/* An exact size can run to 20 characters (#1227): in a mini card it
          breaks inside the number rather than being clipped. */}
      <span data-testid="us-position-held" className="[overflow-wrap:anywhere]">{vm.qtyText}</span>
    </>
  );
}

function PriceWithOdds({
  cents,
  odds,
}: {
  cents: number | null;
  odds: string | null;
}) {
  return (
    <>
      {fmtCents(cents)}
      {odds && <span className="ml-1.5 text-[11px]">{odds}</span>}
    </>
  );
}

/** Full-position context for a sold-down position (#91). A trader who opened
 *  150,000 and sold to 6,297 was seeing only the remainder, with cost and
 *  to-win drifting under it; this strip is the stable anchor. Renders only when
 *  fullPositionSummary() proves a partial sale, so untouched positions keep
 *  today's layout exactly.
 *
 *  Bought/sold/held are labeled rather than stacked as a bare `held/bought`
 *  fraction — the fraction form is the presentation Brendan's 2026-07-22 note
 *  ruled out. */
function FullPositionLine({ full }: { full: FullPositionSummary }) {
  return (
    <span className="text-muted-foreground text-[11px]">
      <span title="Contracts bought over the life of this position — stable through partial sales">
        bought{' '}
        <span className="text-foreground font-mono">{fmtQty(full.originalQty)}</span>
      </span>
      <span className="ml-1.5" title="Contracts sold so far">
        sold <span className="font-mono">{fmtQty(full.soldQty)}</span>
      </span>
      <span className="ml-1.5" title="Contracts still held — what every live figure above is based on">
        held <span className="font-mono">{fmtQty(full.heldQty)}</span>
      </span>
      {full.originalCostDollars != null && (
        <span
          className="ml-1.5"
          title="Lifetime buy cost before fees — unlike the cost stat above, which is the held share plus fees"
        >
          buy cost <span className="font-mono">{fmtUsd(full.originalCostDollars)}</span>
        </span>
      )}
      {full.originalIfWinDollars != null && (
        <span
          className="ml-1.5"
          title="Lifetime payout less lifetime buy cost, before fees, if every bought contract won"
        >
          if all won{' '}
          <span className="font-mono">{fmtUsdSigned(full.originalIfWinDollars)}</span>
        </span>
      )}
    </span>
  );
}

export function CompactPositionItem({
  vm,
  onEdit,
  onOpen,
  detailHref,
  hidePnl,
  showRestingCloseCount = true,
  attribution,
  annotations,
  divergence,
  divergenceFlash = false,
  pinned = false,
  onTogglePin,
  highlighted = false,
  onMouseEnter,
  onMouseLeave,
  viewAnchor,
  close,
}: {
  /** Same-game highlight — true when this row's game is the hovered one. */
  highlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  vm: PositionCardVM;
  onEdit: () => void;
  /** #1006 — a Close control the host draws where the Kalshi pencil sits
   *  (the Position Tracker's international Polymarket Close). Absent, the
   *  card renders exactly as before. */
  close?: ReactNode;
  onOpen?: () => void;
  /** GameDetail page URL — renders the game-detail icon when present.
   *  Row click opens the order panel; the icon is the explicit path to
   *  the detail page (and signals one exists). */
  detailHref?: string | null;
  /** Mask P&L dollars (displaySettings.showPnl off — the page default). */
  hidePnl?: boolean;
  showRestingCloseCount?: boolean;
  /** Optional owning-trader tag shown in the top row (org view only). */
  attribution?: ReactNode;
  /** #978 — the card's annotation controls (Source, Note), in contract
   *  order, or omitted. Sits before Alarm/Pin; rows pass it only as stable
   *  children so an unchanged row still compares equal under `memo`. */
  annotations?: ReactNode;
  /** Cross-venue divergence for this market, or null (badge self-gates). */
  divergence?: DivergenceInfo | null;
  /** Brief one-shot cue when this market crosses into >= 2¢ divergence. */
  divergenceFlash?: boolean;
  /** Pinned-strip membership (PositionTracker). Present onTogglePin renders
   *  the thumbtack; surfaces without a pinned strip just omit it. */
  pinned?: boolean;
  onTogglePin?: () => void;
  /** Stable row identity for #829's reading-position primitive, or undefined
   *  on surfaces that do not restore scroll (no attribute renders then). */
  viewAnchor?: string;
}) {
  const pnlClass =
    hidePnl || vm.pnlDollars == null
      ? 'text-muted-foreground'
      : vm.pnlDollars >= 0
        ? 'text-positive'
        : 'text-negative';

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!onOpen || e.currentTarget !== e.target) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  const compactFull = fullPositionSummary(vm);

  return (
    <div
      data-card-root=""
      data-view-anchor={viewAnchor}
      title={vm.ticker}
      role={onOpen ? 'link' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `Open order book for ${vm.title}` : undefined}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`bg-card px-2.5 py-2 transition-colors hover:bg-muted/20 ${
        onOpen ? 'cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70' : ''
      } ${divergenceFlash ? 'animate-divergence-flash' : ''} ${
        highlighted ? SAME_GAME_HIGHLIGHT : ''
      }`}
    >
      <AnnotatedCompactHeader annotated={annotations != null} layout="compact-position">
      <div className={`flex min-w-0 items-center gap-1.5${annotations != null ? ' g1-annotated-header__row' : ''}`}>
        <SideBadge side={vm.side} team={vm.team} />
        <MarketTypeChip marketType={vm.marketType} />
        <VenueChip venue={vm.venue} />
        {vm.us && <UsAccountWord label={vm.us.accountLabel} accountId={vm.us.accountId} />}
        <div className="min-w-0 flex-1">
          <div className="text-foreground flex items-center gap-1 text-xs font-semibold leading-tight">
            <span className={vm.title === vm.ticker ? 'min-w-0 whitespace-normal break-all' : 'min-w-0 truncate'}>
              <BetTeamTitle title={vm.title} team={vm.titleTeam} />
            </span>
            {vm.familyPending && <FamilyPendingMark />}
            {detailHref && <GameDetailLink href={detailHref} />}
          </div>
          {vm.subtitle && (
            <div className="text-muted-foreground truncate font-mono text-[10px] leading-tight">
              {vm.subtitle}
            </div>
          )}
        </div>
        {annotations != null ? (
          // #979: live values get CONSTANT slots on an annotated card (the
          // #615 w-[Nch] convention), so a divergence alert appearing or the
          // P&L growing can't re-wrap the row and change the card height.
          // Widest alert fmtDivergenceBadge prints, "Poly −88.8¢": 80px (86px
          // at a 20px root); the P&L slot fits "-$123,456.78".
          <span className="inline-flex min-w-[5.25rem] shrink-0 justify-end">
            <DivergenceBadge info={divergence} />
          </span>
        ) : (
          <DivergenceBadge info={divergence} />
        )}
        {attribution && <div className="shrink-0">{attribution}</div>}
        <div
          className={`shrink-0 font-mono text-xs font-semibold ${pnlClass}${annotations != null ? ' min-w-[12ch] text-right' : ''}`}
          title={hidePnl && vm.pnlDollars != null ? 'P&L hidden — click the eye on the Unrealized P&L tile to reveal' : undefined}
        >
          {vm.pnlDollars == null ? '—' : hidePnl ? '•••' : fmtUsdSigned(vm.pnlDollars)}
        </div>
        <AnnotatedControls annotated={annotations != null}>
          {annotations}
          {onTogglePin && <PinToggle pinned={pinned} onToggle={onTogglePin} />}
          {vm.editable && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title="Close / edit position"
              className="text-muted-foreground shrink-0 rounded p-1 transition-colors hover:bg-blue-500/10 hover:text-blue-400"
            >
              <Pencil size={12} />
            </button>
          )}
          {close}
        </AnnotatedControls>
      </div>
      </AnnotatedCompactHeader>

      <div className="mt-1.5 grid grid-cols-3 gap-x-2 gap-y-1 sm:grid-cols-6">
        <CompactStat
          label="qty"
          valueClass={vm.qtyText !== undefined ? 'text-foreground !whitespace-normal !overflow-visible' : undefined}
          value={
            <>
              {positionQty(vm)}
              {showRestingCloseCount && <RestingCloseNote qty={vm.restingCloseQty} />}
            </>
          }
        />
        <CompactStat
          label="avg"
          value={<PriceWithOdds cents={vm.avgCents} odds={vm.avgOdds} />}
          valueClass="text-muted-foreground"
        />
        <CompactStat
          label="now"
          value={<PriceWithOdds cents={vm.nowCents} odds={vm.nowOdds} />}
          valueClass={nowTone(vm.nowCents)}
        />
        <CompactStat label="cost" value={fmtUsd(vm.costDollars)} />
        <CompactStat
          label="cash"
          value={fmtUsd(vm.cashOutDollars)}
          valueClass="text-foreground"
        />
        <CompactStat
          label="if win"
          value={fmtUsdSigned(vm.ifWinDollars)}
          valueClass={vm.ifWinDollars == null || vm.ifWinDollars >= 0 ? 'text-positive' : 'text-negative'}
        />
      </div>
      {compactFull && (
        <div className="mt-1">
          <FullPositionLine full={compactFull} />
        </div>
      )}
    </div>
  );
}

export function PositionCard({
  vm,
  onEdit,
  onOpen,
  detailHref,
  hidePnl,
  showRestingCloseCount = true,
  mini,
  attribution,
  annotations,
  upOdds,
  divergence,
  divergenceFlash = false,
  pinned = false,
  onTogglePin,
  highlighted = false,
  onMouseEnter,
  onMouseLeave,
  viewAnchor,
  footerAction,
  close,
}: {
  /** Same-game highlight — true when this card's game is the hovered one. */
  highlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  vm: PositionCardVM;
  onEdit: () => void;
  onOpen?: () => void;
  /** GameDetail page URL — renders the game-detail icon when present.
   *  Card click opens the order panel; the icon is the explicit path to
   *  the detail page (and signals one exists). */
  detailHref?: string | null;
  /** Mask P&L dollars (displaySettings.showPnl off — the page default). */
  hidePnl?: boolean;
  showRestingCloseCount?: boolean;
  /** Dense grid variant: drop the low-priority stat lines (start, poly,
   *  fees, footer row 2) so ~12 cards fit across a wide monitor. */
  mini?: boolean;
  /** Polymarket book mid for this market, side-native — computed by
   *  PositionTracker's per-second sweep (table parity). Prop name kept from
   *  the retired U/P pair (#1373). */
  upOdds?: UPOdds | null;
  /** Optional header-right slot — the org view passes the owning trader so
   *  attribution rides along in card/mini mode (positions page passes none). */
  attribution?: ReactNode;
  /** #978 — the card's annotation controls (Source, Note), in contract
   *  order, or omitted. Sits before Alarm/Pin; rows pass it only as stable
   *  children so an unchanged row still compares equal under `memo`. */
  annotations?: ReactNode;
  /** Cross-venue divergence for this market, or null (badge self-gates). */
  divergence?: DivergenceInfo | null;
  /** Brief one-shot cue when this market crosses into >= 2¢ divergence. */
  divergenceFlash?: boolean;
  /** Pinned-strip membership (PositionTracker). Present onTogglePin renders
   *  the thumbtack; surfaces without a pinned strip just omit it. */
  pinned?: boolean;
  onTogglePin?: () => void;
  /** Stable row identity for #829's reading-position primitive, or undefined
   *  on surfaces that do not restore scroll (no attribute renders then). */
  viewAnchor?: string;
  /** Optional control beside P&L in footer row 1 (the #982 merged-card
   *  prototype's Merge button). No app surface passes it yet; absent, the
   *  footer renders exactly as before. */
  footerAction?: ReactNode;
  /** #1006 — a Close control the host draws where the Kalshi edit button
   *  sits: footer row 2, or its own line on mini (the Position Tracker's
   *  international Polymarket Close). Absent, the card renders exactly as
   *  before. */
  close?: ReactNode;
}) {
  const pnlClass =
    hidePnl || vm.pnlDollars == null
      ? 'text-muted-foreground'
      : vm.pnlDollars >= 0
        ? 'text-positive'
        : 'text-negative';
  const showFooterRow2 = vm.ifWinDollars != null || vm.editable || close != null;
  const full = fullPositionSummary(vm);
  const pnl = (
    <span className="text-muted-foreground text-xs">
      P&L{' '}
      <span
        className={`font-mono font-semibold ${pnlClass}`}
        title={hidePnl && vm.pnlDollars != null ? 'P&L hidden — click the eye on the Unrealized P&L tile to reveal' : undefined}
      >
        {vm.pnlDollars == null ? '—' : hidePnl ? '•••' : fmtUsdSigned(vm.pnlDollars)}
      </span>
    </span>
  );

  return (
    <CardShell
      viewAnchor={viewAnchor}
      title={vm.ticker}
      className={[
        divergenceFlash && 'animate-divergence-flash',
        highlighted && SAME_GAME_HIGHLIGHT,
      ]
        .filter(Boolean)
        .join(' ')}
      ariaLabel={onOpen ? `Open order book for ${vm.title}` : undefined}
      onClick={onOpen}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <CardHeader
        leftBadges={
          <>
            <SideBadge side={vm.side} team={vm.team} threshold={vm.badgeThreshold} />
            <MarketTypeChip marketType={vm.marketType} />
            <VenueChip venue={vm.venue} />
            {vm.us && <UsAccountWord label={vm.us.accountLabel} accountId={vm.us.accountId} />}
          </>
        }
        rightBadges={
          <>
            {/* Alert cluster — the Poly (violet) divergence pill pops here on
                a >= 2¢ gap; it self-gates. Keep this slot badges-only (+ the
                badge-sized pin toggle): the game-detail link rides the title
                line so a firing badge never crowds it. */}
            <DivergenceBadge info={divergence} />
            {!mini && attribution}
            {annotations == null && onTogglePin && <PinToggle pinned={pinned} onToggle={onTogglePin} />}
          </>
        }
        // #979: only a card showing Source/Note opts into the width-based
        // second row (mini cards always take it): Source → Note → Pin.
        controls={
          annotations != null ? (
            <>
              {annotations}
              {onTogglePin && <PinToggle pinned={pinned} onToggle={onTogglePin} />}
            </>
          ) : undefined
        }
        controlsLayout="position"
        title={<BetTeamTitle title={vm.title} team={vm.titleTeam} />}
        titleAddon={(vm.familyPending || detailHref) && <>
          {vm.familyPending && <FamilyPendingMark />}
          {detailHref && <GameDetailLink href={detailHref} className="-my-0.5" />}
        </>}
        subtitle={vm.subtitle}
        wrapTitle={vm.title === vm.ticker}
      />

      {mini && attribution && (
        <div className="mb-2 min-w-0 [overflow-wrap:anywhere]" data-testid="position-mini-owner">{attribution}</div>
      )}
      <CardBody>
        <StatLine
          label="qty"
          value={
            <>
              {positionQty(vm)}
              {showRestingCloseCount && <RestingCloseNote qty={vm.restingCloseQty} />}
            </>
          }
        />
        <StatLine
          label="avg"
          value={<PriceWithOdds cents={vm.avgCents} odds={vm.avgOdds} />}
        />
        {!mini && <StatLine label="start" value={vm.eventStartLabel || '—'} />}
        {!mini && (
          <StatLine
            label="last fill"
            value={
              <span title={vm.lastFillAt ?? undefined}>
                {fmtLastFillAt(vm.lastFillAt)}
              </span>
            }
          />
        )}
        <StatLine
          label="now"
          value={<PriceWithOdds cents={vm.nowCents} odds={vm.nowOdds} />}
          valueClass={nowTone(vm.nowCents)}
        />
        {/* Poly mid, side-native (parity with the Tracker's poly column;
            replaced the 1m Δ price chip). Always present — unresolved renders
            "—" so the stat grid doesn't reflow as data arrives. */}
        {!mini && <StatLine label={POLY_MID_STAT_LABEL} value={<UPOddsValue odds={upOdds} />} />}
        <StatLine label="cost" value={fmtUsd(vm.costDollars)} />
        {!mini && vm.volume != null && vm.volume > 0 && (
          <StatLine
            label="total vol"
            value={fmtVol(vm.volume)}
            title="Lifetime traded volume since this market opened, not rolling 24-hour volume"
          />
        )}
        {!mini && (
          <StatLine
            label="fees"
            value={vm.feesText ?? (vm.feesCents != null ? fmtUsd(vm.feesCents / 100) : '—')}
            title={vm.us?.feesTitle}
          />
        )}
      </CardBody>

      <CardFooter>
        <div className="flex items-baseline justify-between gap-2">
          {footerAction ? (
            <span className="flex min-w-0 items-center gap-2">
              {pnl}
              {footerAction}
            </span>
          ) : (
            pnl
          )}
          <span className="text-muted-foreground text-xs">
            cash out{' '}
            <span className="text-foreground font-mono font-semibold">
              {fmtUsd(vm.cashOutDollars)}
            </span>
          </span>
          {/* Mini drops footer row 2, which is the edit button's normal
              home — a position you can't close from the dense grid is a
              trap, so the pencil joins row 1 here. */}
          {mini && vm.editable && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title="Close / edit position"
              className="text-muted-foreground shrink-0 rounded p-0.5 transition-colors hover:bg-blue-500/10 hover:text-blue-400"
            >
              <Pencil size={11} />
            </button>
          )}
        </div>
        {/* A worded Close is wider than the pencil: squeezed into mini's row 1
            it wrapped "P&L —" and "cash out —" onto two lines, so mini gives
            it a line of its own. */}
        {mini && close != null && <div className="mt-1 flex justify-end">{close}</div>}
        {!mini && showFooterRow2 && (
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-[11px]">
              {vm.ifWinDollars != null && (
                <>
                  if win{' '}
                  <span className="text-foreground">
                    {fmtUsdSigned(vm.ifWinDollars)}
                  </span>
                  {vm.grossPayoutDollars != null && (
                    <span
                      className="ml-1"
                      title="Gross settlement payout before netting entry cost"
                    >
                      pays {fmtUsd(vm.grossPayoutDollars)}
                    </span>
                  )}
                  {vm.impliedOdds && (
                    <span className="ml-1">({vm.impliedOdds})</span>
                  )}
                </>
              )}
            </span>
            {vm.editable && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                title="Close / edit position"
                className="text-muted-foreground inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] transition-colors hover:bg-blue-500/10 hover:text-blue-400"
              >
                <Pencil size={11} />
                edit
              </button>
            )}
            {close}
          </div>
        )}
        {/* Sold-down positions only — the anchor rides its own footer line so
            it can't be mistaken for the held-share figures above it. Kept on
            mini too: a partially sold position misreading its size is the
            whole point of #91, and mini is where the number is smallest. */}
        {full && (
          <div className="mt-1.5">
            <FullPositionLine full={full} />
          </div>
        )}
      </CardFooter>
    </CardShell>
  );
}
